import { SendOrSaveEmailDraftModel } from "client/Api/Email/respondToEmail";
import { ExchangeEmailId } from "client/Api/Email/common";
import { ArgumentNotSuppliedError } from "client_legacy/ndaw/errors";
import {
	SaveDraftResponseModel,
	saveOrUpdateExchangeDraft
} from "client/Api/Email/email";

export type Options = {
	intervalInSeconds: number;
	modelFactory: () => SendOrSaveEmailDraftModel;
	idChanged: (id: string) => void;
};

export class DraftAutoSaver {
	private readonly _options: Options;
	private _state: SaveState;
	private _intervalHandler: SetTimeoutHandle | null;
	private _lastPersistedModel: SendOrSaveEmailDraftModel | undefined;

	constructor(options: Options) {
		this._options = options;
		this._state = SaveState.Stopped;
		this._intervalHandler = null;
	}

	public start() {
		if (this._state !== SaveState.Stopped) {
			throw new Error("Was already started");
		}

		this.internalStart();
	}

	public stop() {
		this.internalStop(SaveState.Stopped);
	}

	private internalStart() {
		this._state = SaveState.Started;
		this._intervalHandler = setInterval(
			() => this.saveWhenNeeded(),
			this._options.intervalInSeconds * 1000
		);
	}

	private internalStop(newState: SaveState.Stopped | SaveState.WaitingForRequestToFinish) {
		if (this._intervalHandler) {
			clearInterval(this._intervalHandler);
			this._intervalHandler = null;
		}
		this._state = newState;
	}

	private saveWhenNeeded() {
		const model: SendOrSaveEmailDraftModel = this._options.modelFactory();
		const isNew: boolean = !model.exchangeId.id;
		const isSaveNeeded: boolean = isNew
			|| !this._lastPersistedModel
			|| this.isDifferent(this._lastPersistedModel, model);

		if (!isSaveNeeded) {
			return;
		}

		this.internalStop(SaveState.WaitingForRequestToFinish);

		this.saveInternal(model)
			.finally(() => {
				// Only resume the interval when we are still in a waiting state
				if (this._state === SaveState.WaitingForRequestToFinish) {
					this.internalStart();
				}
			});
	}

	public forceSave() {
		const model: SendOrSaveEmailDraftModel = this._options.modelFactory();
		this.saveInternal(model);
	}

	private async saveInternal(model: SendOrSaveEmailDraftModel): Promise<SaveDraftResponseModel> {
		if (!model) {
			throw new ArgumentNotSuppliedError("model");
		}

		try {
			const response: SaveDraftResponseModel = await saveOrUpdateExchangeDraft(model);
			const exchangeId: ExchangeEmailId = response.exchangeId;

			if (model.exchangeId.id !== exchangeId.id) {
				model.exchangeId = { id: exchangeId.id };
				this._options.idChanged(model.exchangeId.id);
			}

			this._lastPersistedModel = model;
			return response;
		} catch(e) {
			console.error("Save draft to exchange failed", e);
			throw e;
		}
	}

	private isDifferent(lhs: SendOrSaveEmailDraftModel, rhs: SendOrSaveEmailDraftModel): boolean {
		if (lhs.exchangeId !== rhs.exchangeId)
		{
			if (lhs.exchangeId === undefined || rhs.exchangeId === undefined
				|| lhs.exchangeId === null || rhs.exchangeId === null
			) {
				return true;
			}

			if (lhs.exchangeId.hasValue !== rhs.exchangeId.hasValue
				|| lhs.exchangeId.id !== rhs.exchangeId.id
			) {
				return true;
			}
		}

		if (isSmtpAddressDifferent(lhs.smtpAddress, rhs.smtpAddress)) {
			return true;
		}

		return lhs.fromAddress !== rhs.fromAddress
			|| lhs.to !== rhs.to
			|| lhs.cc !== rhs.cc
			|| lhs.bcc !== rhs.bcc
			|| lhs.subject !== rhs.subject
			|| lhs.body !== rhs.body
			|| lhs.importance !== rhs.importance;
	}
}

function isSmtpAddressDifferent(
	lhs: OptionalModel<string>,
	rhs: OptionalModel<string>
): boolean {
	const lhsValue: string | null = lhs.isSet
		? lhs.value
		: null;

	const rhsValue: string | null = rhs.isSet
		? rhs.value
		: null;

	return lhsValue !== rhsValue;
}

enum SaveState {
	Stopped = 0,
	Started = 1,
	WaitingForRequestToFinish = 2
}